/**
 * The server function + React Query wiring that resolves {@link RequestPreferences} from
 * the incoming request during SSR and ships the SAME value to the client via the router's
 * Query-dehydration bridge (so the first client render matches the server exactly).
 *
 * The handler runs server-only (createServerFn strips it + its `@tanstack/react-start/server`
 * import from the client bundle), so reading the request here is safe. It's invoked once
 * from the root route loader; `staleTime: Infinity` means the client never refetches it.
 */
import { createServerFn } from '@tanstack/react-start';
import { getRequest, getRequestHeader } from '@tanstack/react-start/server';
import { queryOptions } from '@tanstack/react-query';
import { logger } from '@repo/shared/common';
import {
  DEFAULT_PREFERENCES,
  PREFERENCES_QUERY_KEY,
  resolvePreferences,
  type RequestPreferences,
} from './preferences';

const log = logger.child({ module: 'preferences' });

/**
 * Read the incoming request's `Cookie` + `Accept-Language`, plus Cloudflare's geo
 * `request.cf.timezone` (present on deployed Workers; absent in local dev → falls back to
 * UTC), and resolve the visitor's locale + timezone. Never throws — returns the safe
 * default if the request context isn't available (e.g. under test).
 */
export const getRequestPreferences = createServerFn({ method: 'GET' }).handler(
  (): RequestPreferences => {
    try {
      const request = getRequest();
      const cookieHeader = request?.headers.get('cookie') ?? null;
      const acceptLanguage = getRequestHeader('accept-language') ?? null;
      // `cf` is a Cloudflare Workers extension of the Request; typed loosely because it's
      // not in the standard Request type and is undefined off Cloudflare / in local dev.
      const cfTimeZone =
        (request as { cf?: { timezone?: string } } | undefined)?.cf?.timezone ?? null;
      return resolvePreferences({ cookieHeader, acceptLanguage, cfTimeZone });
    } catch (err) {
      log.warn('Failed to resolve request preferences; using defaults', {
        error: err instanceof Error ? err.message : String(err),
      });
      return DEFAULT_PREFERENCES;
    }
  },
);

/**
 * Query options for the request-resolved preferences. Prefetched in the root route loader
 * (server-awaited → dehydrated into the HTML), read on the client from the warm cache.
 * Never stale (a per-request constant), never garbage-collected during the session.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- returns TanStack's opaque `queryOptions` brand; the RequestPreferences generic keeps callers typed.
export function preferencesQueryOptions() {
  return queryOptions<RequestPreferences>({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: () => getRequestPreferences(),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}
